import { useContext, useMemo } from "react";
import {
  CellClassParams,
  CellRendererSelectorResult,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueGetterParams
} from "ag-grid-enterprise";
import { useParams } from "react-router";
import {
  COMMENTS_EXPORT_COLUMN_ID,
  formatCommentsForExport
} from "utils/comments-export";
import {
  coldefDateField,
  coldefHiddenField,
  coldefLinkedActivities,
  coldefSingleField,
  coldefUser,
  dateTimeAuditColumnOverrides
} from "entity-app/shared-components/log-render-components/col-defs";
import {
  ArrayStringGetQuickFilterText,
  featureStatusCellRenderer,
  logGroupCellRenderer,
  materialEarlyReleaseCellRenderer,
  MaterialLogGroupRowPlaceholderCell,
  MaterialSequenceIdLinkCellRenderer,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "entity-app/shared-components/log-render-components/cell-renders";
import {
  CellValueRendererWithEditIcon,
  dueDateCellRenderer,
  LBSCellRenderer,
  leadTimeHeaderComponent,
  materialActionCellRenderer,
  materialDueDateHeaderComponent,
  MaterialLinksCellRenderer,
  materialSourceCellRenderer,
  nextDeadlineCellRenderer,
  riskCellRenderer,
  specSectionLabel,
  SubmittalLinksEntityCellRenderer
} from "components/cell-renders";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";
import { EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { MeasurementSelectEditor } from "components/cell-editor/measurement-select-editor";
import { FeatureSpecSectionCellEditor } from "components/cell-editor/spec-section-editor";
import { LBSFieldCellEditor } from "components/cell-editor/lbs-cell-editor";
import { RiskLevelType } from "components/date-block/models";
import WarrantyCellEditor, {
  WarrantyValueFormatter
} from "components/cell-editor/warranty-cell-editor";
import ShippingLocationCellEditor from "components/cell-editor/shipping-location-cell-editor";
import { DateUtils } from "utils/dateutils";
import CustomTreeFilter from "components/grid-filters/custom-tree-filter";
import CustomDateCellEditor from "components/cell-editor/date-cell-editor";
import { ProjectContext } from "context/ProjectProvider";
import { buildCustomFieldColumnDefs } from "components/custom-fields/log-page";
import { useEnumValues } from "hooks/enum-values";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator,
  linkedMaterialsComparator,
  linkedLocationsComparator,
  linkedSubmittalsComparator,
  linkedTasksComparator
} from "utils/grid-utils";
import LinkedSubmittalInnerHeader from "../../../components/header-ag-grid-component/custom-ag-grid-header-component";
import { TMaterialLog } from "../model";
import { MATERIAL_ID_COL_WIDTH } from "../material-id-column-width";
import { DashboardAiInsightsType } from "pages/project-dashboard/models";

const materialCharacteristicsColIds = new Set([
  "shipping_location",
  "manufacturer",
  "part_number",
  "serial_number",
  "warranty",
  "quantity_unit",
  "material_size_unit"
]);

function isMaterialLogAutoGroupColumn(params: ICellRendererParams): boolean {
  const colId = params.column?.getColId?.() ?? "";
  if (colId.startsWith("ag-Grid-AutoColumn")) return true;
  return params.colDef?.colId === "group_column";
}

function materialLogDefaultCellRendererSelector(
  params: ICellRendererParams<TMaterialLog>
): CellRendererSelectorResult | undefined {
  if (params.node?.group) {
    if (isMaterialLogAutoGroupColumn(params)) {
      return undefined;
    }
    return { component: MaterialLogGroupRowPlaceholderCell };
  }
  const { colDef } = params;
  if (colDef?.cellRenderer) {
    return {
      component: colDef.cellRenderer,
      params: colDef.cellRendererParams
    };
  }
  return {
    component: CellValueRendererWithEditIcon,
    params: colDef?.cellRendererParams
  };
}

export const useMaterialLogAgGrid = (props: {
  featureKey: string;
  milestonesColumns: any;
  cannotWriteAccessOfMaterial: boolean;
  materialHeaderMap: any;
  isLBSFeatureEnabled: boolean;
  canSetLBSForMaterial: boolean;
  isMaterialCsiClassificationEnabled: boolean;
}) => {
  const {
    featureKey,
    milestonesColumns,
    cannotWriteAccessOfMaterial,
    materialHeaderMap,
    isLBSFeatureEnabled,
    canSetLBSForMaterial,
    isMaterialCsiClassificationEnabled
  } = props;

  const {
    columnHeaders: { materialCustomColumns }
  } = useContext(ProjectContext);

  const { enumOptionsMap } = useEnumValues(FeatureTypes.MATERIALS);

  const coldefCustomFields = useMemo(
    () => buildCustomFieldColumnDefs(materialCustomColumns, enumOptionsMap),
    [materialCustomColumns, enumOptionsMap]
  );

  const { projectId } = useParams<{ projectId: string }>();
  const agGridCheckFieldEditable = (params: CellClassParams<any, any>) => {
    const { data, colDef } = params;
    const colId = colDef.colId || "";
    const fieldEditOnlyWhenNotStarted = [
      "planned_float",
      "date_block_template_name"
    ];
    if (
      fieldEditOnlyWhenNotStarted.includes(colId) &&
      data?.workflow_status !== EWorkflowStatusDataBlock.WF_NOT_STARTED
    )
      return false;
    const fieldEditWhenNotComplete = ["manual_entry_date"];
    if (
      fieldEditWhenNotComplete.includes(colId) &&
      data?.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED
    )
      return false;
    return true;
  };

  const inlineEditPermissions = (
    params: CellClassParams<any, any>
  ): boolean => {
    if (!cannotWriteAccessOfMaterial) return true;
    const { colDef } = params;
    const colId = colDef.colId || "";
    if (materialCharacteristicsColIds.has(colId)) {
      return params.context.canInlineEditMaterialCharacteristics;
    }
    return false;
  };

  const isGridCellEditable = (params: CellClassParams<any, any>) => {
    return (
      params?.colDef?.editable &&
      inlineEditPermissions(params) &&
      agGridCheckFieldEditable(params)
    );
  };

  const defaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        minWidth: 100,
        cellRenderer: CellValueRendererWithEditIcon,
        cellRendererSelector: materialLogDefaultCellRendererSelector,
        cellEditorPopup: false,
        comparator: comparatorWithEmptyAtBottom
      }) as ColDef<TMaterialLog>,
    []
  );

  const columnDefs = useMemo(
    () =>
      [
        coldefSingleField({
          colId: "material_id",
          field: "sequence_id",
          headerName: materialHeaderMap?.material_id,
          headerTooltip: materialHeaderMap?.material_id,
          pinned: "left",
          lockPosition: "left",
          initialWidth: MATERIAL_ID_COL_WIDTH,
          filter: false,
          cellRenderer: MaterialSequenceIdLinkCellRenderer,
          cellRendererParams: {
            projectId,
            featureKey
          }
        }),
        coldefSingleField({
          colId: "name",
          field: "title",
          headerName: materialHeaderMap?.name,
          headerTooltip: materialHeaderMap?.name,
          initialWidth: 180,
          pinned: "left",
          lockPosition: "left",
          cellRenderer: twoRowCellRenderer,
          cellRendererParams: {
            featureKey,
            projectId
          },
          getQuickFilterText: twoRowGetQuickFilterText,
          lockVisible: true,
          editable: true,
          filter: true,
          cellClassRules: {
            hyperlinks: () => true
          }
        }),
        coldefHiddenField({
          field: "description",
          headerName: materialHeaderMap?.description,
          pinned: "left",
          lockPosition: "left"
        }),
        coldefSingleField({
          colId: "status",
          field: "milestone_state",
          headerName: materialHeaderMap?.status,
          lockVisible: true,
          headerTooltip: materialHeaderMap?.status,
          initialWidth: 200,
          minWidth: 200,
          tooltipField: undefined,
          tooltipValueGetter: undefined,
          editable: true,
          filter: "agSetColumnFilter",
          cellRenderer: featureStatusCellRenderer
        }),
        coldefSingleField({
          field: "ready_for_release",
          colId: "ready_for_release",
          headerName:
            materialHeaderMap?.ready_for_release || "Ready for Release",
          headerTooltip:
            materialHeaderMap?.ready_for_release || "Ready for Release",
          initialWidth: 180,
          editable: false,
          filter: "agSetColumnFilter",
          valueGetter: (params: ValueGetterParams) => {
            return params.data?.ready_for_release ? "Ready" : "Not Ready";
          }
        }),
        coldefSingleField({
          field: "material_released_early",
          headerName: materialHeaderMap?.wf_override_reason || "Released Early",
          headerTooltip:
            materialHeaderMap?.wf_override_reason || "Released Early",
          initialWidth: 160,
          editable: false,
          cellRenderer: materialEarlyReleaseCellRenderer,
          filter: "agSetColumnFilter",
          valueGetter: ({ data }: any) => {
            if (data?.material_released_early) return "Yes";
            return "";
          }
        }),
        coldefHiddenField({
          colId: "released_early_msg",
          field: "material_released_early",
          headerName: "Reason Cited For Early Release"
        }),
        coldefDateField({
          field: "planned_next_milestone_date",
          colId: "planned_next_milestone_date",
          headerName: materialHeaderMap?.planned_next_milestone_date,
          headerTooltip: materialHeaderMap?.planned_next_milestone_date,
          initialWidth: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params?.data?.date_block?.planned_next_milestone_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "projected_next_milestone_date",
          colId: "projected_next_milestone_date",
          headerName: materialHeaderMap?.projected_next_milestone_date,
          headerTooltip: materialHeaderMap?.projected_next_milestone_date,
          initialWidth: 160,
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params?.data?.date_block?.projected_next_milestone_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "next_planned_deadline",
          headerName: materialHeaderMap?.next_planned_deadline,
          headerTooltip: materialHeaderMap?.next_planned_deadline,
          initialWidth: 160,
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefSingleField({
          field: "next_action",
          headerName: materialHeaderMap?.next_action || "",
          headerTooltip: materialHeaderMap?.next_action || "",
          initialWidth: 200,
          editable: false,
          filter: false
        }),
        coldefDateField({
          colId: "manual_entry_date",
          field: "manual_entry_date",
          headerName: materialHeaderMap?.manual_entry_date || "Deadline Date",
          headerTooltip:
            materialHeaderMap?.manual_entry_date || "Deadline Date",
          editable: true,
          cellRenderer: CellValueRendererWithEditIcon,
          cellEditor: CustomDateCellEditor,
          valueGetter: undefined,
          valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
          cellEditorParams: {
            placeholder: `Select ${
              materialHeaderMap?.manual_entry_date || "Deadline Date"
            }`
          }
        }),
        coldefDateField({
          field: "date_block.final_deadline_date",
          colId: "final_deadline_date",
          headerName: materialHeaderMap?.final_deadline_date || "Deadline",
          headerTooltip: materialHeaderMap?.final_deadline_date || "Deadline",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params?.data?.date_block?.final_deadline_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "date_block.projected_last_milestone_date",
          colId: "projected_last_milestone_date",
          headerName:
            materialHeaderMap?.projected_last_milestone_date ||
            "Projected Completion Date",
          headerTooltip:
            materialHeaderMap?.projected_last_milestone_date ||
            "Projected Completion Date",
          valueGetter: (params: ValueGetterParams) => {
            return DateUtils.format(
              params?.data?.date_block?.projected_last_milestone_date
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            return nextDeadlineCellRenderer(params, false);
          }
        }),
        coldefDateField({
          field: "due_date",
          headerName: materialHeaderMap?.due_date,
          headerTooltip: materialHeaderMap?.due_date,
          initialWidth: 160,
          cellRenderer: (params: ICellRendererParams) => {
            return dueDateCellRenderer(params, false);
          },
          headerComponentParams: materialDueDateHeaderComponent
        }),
        coldefSingleField({
          field: "risk_level",
          headerName: materialHeaderMap?.risk_level,
          headerTooltip: materialHeaderMap?.risk_level,
          initialWidth: 100,
          comparator: riskLevelComparator,
          filter: "agSetColumnFilter",
          cellRenderer: (params: ICellRendererParams) => {
            return riskCellRenderer(params, false);
          },
          cellClassRules: {
            highRisk: ({ value }) => {
              return value === RiskLevelType.High;
            },
            mediumRisk: ({ value }) => {
              return value === RiskLevelType.Medium;
            },
            lowRisk: ({ value }) => {
              return value === RiskLevelType.Low;
            }
          }
        }),
        coldefSingleField({
          colId: "planned_float",
          field: "pre_WF_start_float",
          headerName: "Material Planned Float",
          headerTooltip: "Material Planned Float",
          initialWidth: 130,
          editable: true,
          cellEditor: "agNumberCellEditor",
          cellEditorParams: numberCellEditorParams,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefSingleField({
          field: "effective_float",
          headerName: "Material Actual Float",
          headerTooltip: "Material Actual Float",
          initialWidth: 130,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams
        }),
        coldefSingleField({
          field: "lead_time",
          headerName: materialHeaderMap?.lead_time || "Total Lead Time",
          headerTooltip: materialHeaderMap?.lead_time || "Total Lead Time",
          initialWidth: 100,
          editable: false,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          headerComponentParams: leadTimeHeaderComponent
        }),
        coldefSingleField({
          colId: "custom_spec_section",
          field: "spec_section",
          headerName: materialHeaderMap?.spec_section_no,
          valueGetter: ({ data }) => {
            if (!data?.spec_section?.length) return "";
            const spec = data?.spec_section[0];
            const specLabel = specSectionLabel({
              specNumber: spec?.number,
              specName: spec?.name
            });

            return specLabel;
          },
          editable: true,
          cellEditor: FeatureSpecSectionCellEditor,
          initialWidth: 200,
          headerTooltip: materialHeaderMap?.spec_section_no
        }),
        ...(isMaterialCsiClassificationEnabled
          ? [
              coldefSingleField({
                colId: "division",
                field: "division",
                headerName: materialHeaderMap?.division || "Division",
                filter: "agSetColumnFilter",
                valueGetter: ({ data }: any) => {
                  if (!data?.division) return "";
                  return `${data?.division} - ${data?.division_name}`;
                }
              })
            ]
          : []),
        coldefSingleField({
          field: "purchase_order",
          headerName: materialHeaderMap?.purchase_order || "",
          headerTooltip: materialHeaderMap?.purchase_order || "",
          initialWidth: 160,
          editable: true
        }),
        coldefSingleField({
          colId: "material_tag",
          field: "tag_name",
          headerName: materialHeaderMap?.tag_name || "",
          headerTooltip: materialHeaderMap?.tag_name || "",
          initialWidth: 160,
          editable: true
        }),
        ...(isLBSFeatureEnabled
          ? [
              coldefSingleField({
                field: "locations",
                headerName: materialHeaderMap?.locations || "",
                headerTooltip: materialHeaderMap?.locations || "",
                initialWidth: 250,
                minWidth: 250,
                maxWidth: 600,
                valueGetter: ({ data }) => {
                  if (!data?.locations) return [];
                  return data?.locations?.map((loc: any) => ({
                    id: loc.id,
                    name: loc.path,
                    path: loc.path
                  }));
                },
                filter: CustomTreeFilter,
                editable: canSetLBSForMaterial,
                cellRenderer: LBSCellRenderer,
                cellEditor: LBSFieldCellEditor,
                getQuickFilterText: ArrayStringGetQuickFilterText,
                comparator: linkedLocationsComparator
              })
            ]
          : []),
        coldefUser({
          def: {
            colId: "date_block_template_name",
            headerName: materialHeaderMap?.date_block_template_name,
            headerTooltip: materialHeaderMap?.date_block_template_name,
            editable: true,
            initialWidth: 180
          },
          user: {
            id: "template.id",
            firstName: "template.name",
            lastName: "",
            dropdownType: "Template_dropdown",
            featureTypeId: FeatureTypes.MATERIALS
          }
        }),
        coldefSingleField({
          colId: "quantity_unit",
          field: "quantity",
          headerName: materialHeaderMap?.quantity || "",
          headerTooltip: materialHeaderMap?.quantity || "",
          initialWidth: 180,
          minWidth: 180,
          editable: true, // materialCharacteristicsCellsEditable,
          valueGetter: (param: any) =>
            `${param?.data?.quantity ?? ""} ${
              param?.data?.material_quantity_unit ?? ""
            }`.trim(),
          cellEditor: MeasurementSelectEditor,
          cellEditorParams: {
            type: "regular-select",
            dataMapVar: "material_quantity_unit",
            setKeyId: "quantity_unit_id",
            unitValueProp: "quantity",
            unit: "material_quantity_unit"
          }
        }),
        coldefUser({
          def: {
            colId: "trade_partner",
            headerName: materialHeaderMap?.trade_partner,
            headerTooltip: materialHeaderMap?.trade_partner,
            editable: true,
            initialWidth: 220,
            enableRowGroup: true
          },
          user: {
            id: "responsible_contractor.id",
            firstName: "responsible_contractor.name",
            lastName: "",
            dropdownType: "RC_dropdown"
          }
        }),

        coldefUser({
          def: {
            colId: "assignee",
            field: "assignee_user",
            headerName: materialHeaderMap?.assignee,
            headerTooltip: materialHeaderMap?.assignee,
            editable: true,
            initialWidth: 180,
            enableRowGroup: true
          },
          user: {
            id: "assignee_user.id",
            firstName: "assignee_user.first_name",
            lastName: "assignee_user.last_name",
            statusId: "assignee_user.status_id",
            dropdownType: "RC_ASSIGNEE_dropdown",
            dependentId: "responsible_contractor.id",
            dependentNotFoundContent: "First select a Responsible Contractor",
            companyObjField: "assignee_user.company"
          }
        }),
        coldefUser({
          def: {
            colId: "gc_representative",
            headerName: materialHeaderMap?.gc_representative,
            headerTooltip: materialHeaderMap?.gc_representative,
            editable: true,
            initialWidth: 180
          },
          user: {
            id: "gc_representative.id",
            firstName: "gc_representative.first_name",
            lastName: "gc_representative.last_name",
            statusId: "gc_representative.status_id",
            companyObjField: "gc_representative.company"
          }
        }),

        coldefSingleField({
          colId: "shipping_location",
          field: "shipping_location.label",
          headerName: materialHeaderMap?.shipping_location || "",
          headerTooltip: materialHeaderMap?.shipping_location || "",
          editable: true,
          cellEditor: ShippingLocationCellEditor,
          filter: "agSetColumnFilter"
        }),
        coldefSingleField({
          field: "manufacturer",
          headerName: materialHeaderMap?.manufacturer || "",
          headerTooltip: materialHeaderMap?.manufacturer || "",
          editable: true,
          initialWidth: 140
        }),

        coldefLinkedActivities({
          field: "tasks",
          colId: "linked_tasks",
          cellDataType: false,
          headerName: materialHeaderMap?.linked_activities || "",
          headerTooltip: materialHeaderMap?.linked_activities || "",
          cellClass: "excelWrapText",
          comparator: linkedTasksComparator
        }),
        coldefSingleField({
          field: "materials",
          colId: "linked_materials",
          cellDataType: false,
          headerName: materialHeaderMap?.linked_materials || "",
          headerTooltip: materialHeaderMap?.linked_materials || "",
          cellClass: "excelWrapText",
          initialWidth: 400,
          minWidth: 400,
          cellRenderer: MaterialLinksCellRenderer,
          valueGetter: ({ data }: any) => {
            return data?.materials
              .filter(
                (item: any) =>
                  item.feature_type === FeatureTypes.MATERIALS && !item.implicit
              )
              ?.map((material: any) => {
                return material;
              });
          },
          comparator: linkedMaterialsComparator,
          filterParams: {
            valueFormatter: ({ value }: any) => {
              return value ? "Materials Linked" : "No Materials Linked";
            },
            keyCreator: ({ value }: any) => {
              return value ? "Materials Linked" : "No Materials Linked";
            }
          }
        }),
        coldefSingleField({
          colId: "linked_submittals",
          field: "linked_items",
          headerName: materialHeaderMap?.linked_submittals || "",
          cellDataType: false,
          cellClass: "excelWrapText",
          headerComponentParams: {
            innerHeaderComponent: LinkedSubmittalInnerHeader
          },
          initialWidth: 400,
          minWidth: 400,
          resizable: false,
          cellRenderer: SubmittalLinksEntityCellRenderer,
          editable: false,
          comparator: linkedSubmittalsComparator,
          filterValueGetter: ({ data }: any) => {
            const submittals = data?.linked_items || [];
            const hasSubmittal = submittals?.some(
              (x: any) => x?.feature_type === FeatureTypes.SUBMITTALS
            );
            return hasSubmittal ? "Submittal Linked" : "No Submittals Linked";
          }
        }),
        coldefHiddenField({
          colId: "is_linked_void_submittal",
          headerName: "Voided/Deleted Submittals",
          filter: "agSetColumnFilter",
          valueGetter: ({ data }: any) => {
            const labels: string[] = [];
            const linkedItems = data?.linked_items || [];
            if (
              linkedItems.some(
                (submittal: any) =>
                  submittal.workflow_status === EWorkflowStatusDataBlock.VOID
              )
            ) {
              labels.push("Voided Submittals");
            }
            if (
              linkedItems.some(
                (submittal: any) => submittal.deleted_from_source
              )
            ) {
              labels.push("Deleted Submittals");
            }
            return labels;
          }
        }),
        coldefHiddenField({
          colId: "workflow_status",
          field: "workflow_status",
          headerName: "Workflow Status",
          filter: "agSetColumnFilter",
          menuTabs: ["filterMenuTab"]
        }),
        coldefHiddenField({
          field: "ai_insights",
          headerName: "AI Insights",
          filter: "agSetColumnFilter",
          valueGetter: ({ data }: any) => {
            const aiInsights = data?.ai_insights as DashboardAiInsightsType;
            const {
              field_measurement_required,
              mockups_required,
              mockups_proof,
              recessed_condition
            } = aiInsights || {};

            const names: string[] = [];
            if (field_measurement_required)
              names.push("Field Measurement Required");
            if (mockups_required) names.push("Mockups Required");
            if (mockups_proof) names.push("Mockups Proof");
            if (recessed_condition) names.push("Recessed Condition");

            return names;
          }
        }),
        coldefSingleField({
          field: "material_size_unit",
          headerName: materialHeaderMap?.material_size || "",
          headerTooltip: materialHeaderMap?.material_size || "",
          editable: true,
          initialWidth: 180,
          minWidth: 180,
          valueGetter: (param: any) =>
            `${param?.data?.material_size ?? ""} ${
              param?.data?.material_size_unit || ""
            }`.trim(),
          cellEditor: MeasurementSelectEditor,
          cellEditorParams: {
            type: "regular-select",
            dataMapVar: "material_quantity_unit",
            setKeyId: "size_unit_id",
            unitValueProp: "material_size",
            unit: "material_size_unit"
          }
        }),
        coldefSingleField({
          field: "part_number",
          headerName: materialHeaderMap?.part_number || "",
          headerTooltip: materialHeaderMap?.part_number || "",
          editable: true,
          initialWidth: 140
        }),
        coldefSingleField({
          field: "serial_number",
          headerName: materialHeaderMap?.serial_number || "",
          headerTooltip: materialHeaderMap?.serial_number || "",
          editable: true,
          initialWidth: 160
        }),
        coldefSingleField({
          field: "warranty",
          headerName: materialHeaderMap?.warranty || "",
          headerTooltip: materialHeaderMap?.warranty || "",
          valueFormatter: WarrantyValueFormatter,
          editable: true,
          initialWidth: 190,
          filter: "agNumberColumnFilter",
          filterParams: numberFilterParams,
          cellEditor: WarrantyCellEditor
        }),
        coldefDateField({
          field: "last_manual_updated_at",
          colId: "last_manual_updated_at",
          headerName:
            materialHeaderMap?.last_manual_updated_at || "Last Updated by User",
          headerTooltip:
            materialHeaderMap?.last_manual_updated_at || "Last Updated by User",
          ...dateTimeAuditColumnOverrides("last_manual_updated_at")
        }),
        coldefDateField({
          field: "last_system_updated_at",
          colId: "last_system_updated_at",
          headerName:
            materialHeaderMap?.last_system_updated_at ||
            "Last Updated by System",
          headerTooltip:
            materialHeaderMap?.last_system_updated_at ||
            "Last Updated by System",
          ...dateTimeAuditColumnOverrides("last_system_updated_at")
        }),
        coldefSingleField({
          field: "source",
          headerName: materialHeaderMap?.source || "Source",
          headerTooltip: materialHeaderMap?.source || "Source",
          initialWidth: 140,
          editable: false,
          cellRenderer: materialSourceCellRenderer
        }),
        coldefSingleField({
          colId: "action",
          headerName: "Actions",
          headerTooltip: "Actions",
          pinned: "right",
          initialWidth: 120,
          editable: false,
          suppressColumnsToolPanel: true,
          cellRenderer: materialActionCellRenderer,
          filter: false,
          sortable: false,
          menuTabs: [],
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true
        }),
        ...milestonesColumns.columnDefsForDateBlock,
        ...coldefCustomFields,
        // Export-only: hidden in the grid; included in CSV/Excel/print only
        // when the user opts into comments.
        coldefHiddenField({
          colId: COMMENTS_EXPORT_COLUMN_ID,
          field: "comments",
          headerName: materialHeaderMap?.comments || "Comments",
          cellClass: ["excelWrapText", "!whitespace-pre-line"],
          wrapText: true,
          autoHeight: true,
          width: 360,
          suppressAutoSize: true,
          valueGetter: ({ data }: any) =>
            formatCommentsForExport(data?.comments)
        })
      ] as (ColDef<TMaterialLog> | ColGroupDef<TMaterialLog>)[],
    [
      materialHeaderMap,
      projectId,
      featureKey,
      isLBSFeatureEnabled,
      canSetLBSForMaterial,
      isMaterialCsiClassificationEnabled,
      milestonesColumns.columnDefsForDateBlock,
      coldefCustomFields
    ]
  );

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 200,
      context: { autoGroupColId: "group_column" },
      filter: "agGroupColumnFilter",
      sortable: true,
      pinned: "left",
      lockPosition: "left",
      suppressMovable: true,
      headerValueGetter: (params: any) => {
        const rowGroupColumns = params.api.getRowGroupColumns();
        if (rowGroupColumns.length === 0) return "No Grouping";

        const groupedHeaders = rowGroupColumns
          .map((col: any) => {
            const colDef = col?.getColDef?.();
            // For our grouped columns we set `headerName` directly (string).
            return colDef?.headerName || col?.getColId?.();
          })
          .filter(Boolean);

        return `Group by ${groupedHeaders.join(", ")}`;
      },
      cellRendererParams: {
        // suppressCount: true,
        innerRenderer: logGroupCellRenderer
      }
    };
  }, []);

  return {
    columnDefs,
    defaultColDef,
    autoGroupColumnDef,
    milestonesColumns,
    isGridCellEditable
  };
};
